<div class="rt-card" [class.reply]="noteIsReply">
  @if (note().visibility?.level !== VisibilityLevel.Public && !note().replyToId) {
    <div class="visibility">
      <rt-visibility-selector-container
        [visibility]="note().visibility!"
        [editable]="false"
        avatarSize="small-x"
      />
    </div>
  }

  <div
    class="note"
    [class.reply]="noteIsReply"
    [class.editing]="editing()"
    (mouseenter)="setHeaderButtonsVisibility(true)"
    (mouseleave)="setHeaderButtonsVisibility(false)"
  >
    @if (!editing()) {
      <div class="precontent">
        @if (noteIsReply) {
          <div class="indicator"></div>
        }
        <div class="avatar-wrapper" [class.smaller]="noteIsReply">
          <rt-note-avatar
            [author]="author()"
            [triggered]="note().triggered ?? null"
            [size]="noteIsReply ? 'small-x' : 'small'"
          />
        </div>
        @if (note().replies?.length || hasConnector) {
          <div class="rectangle" [class.connector]="hasConnector"></div>
        }
      </div>
      <div class="content">
        <div class="entry">
          <div
            class="header"
            layout
            layout-space-between
            layout-align-items-center
            padding-bottom-x
          >
            <div class="author-container">
              <div class="author">
                <span>{{
                  !note().triggered ? authorName() : (notesTranslations.automatedAction | translate)
                }}</span>
              </div>
              @if (author()?.isGuest) {
                <rt-badge
                  color="optional"
                  [text]="notesTranslations.guest | translate"
                  margin-left-small-x
                />
              }
            </div>
            <div layout>
              <rt-group
                [class.visible]="showHeaderButtons && !isReadOnly"
                class="header-buttons"
                variant="stacked"
                padding-left-small-xx
              >
                <ng-container *ngTemplateOutlet="actionButtons" />
              </rt-group>
              @if (note().pinnedAt) {
                <rt-icon-button
                  [rtTooltip]="notesTranslations.unpin | translate"
                  (click)="unpinNote()"
                  variant="plain"
                  iconName="pin"
                  shape="square"
                  size="small"
                />
              }

              <rt-date
                [time]="note().createdAt!"
                [tiny]="true"
                class="created-at"
                margin-left-small-xx
              />
            </div>
          </div>

          <div
            #noteRenderer="prosemirrorRender"
            prosemirrorDefaultConfig
            [prosemirrorRender]="note().bodyJson"
            [prosemirrorRenderConfig]="ProsemirrorConfig"
            (prosemirrorEvents)="onWysiwygEvent($event)"
          ></div>
        </div>

        <rt-group class="attachments" variant="horizontal">
          @for (attachment of note().attachments; track attachment.id) {
            <rt-attachment-item [attachment]="attachment" type="compact-card" />
          }
        </rt-group>

        @if (!isReadOnly) {
          <div class="meta" margin-top-small-xx>
            <div layout-vertical layout-align-items-start>
              <rt-note-reactions-container
                [noteId]="note().id!"
                [reactions]="note().reactions!"
                [projectedContentTpl]="replyButton"
              />
              <ng-template #replyButton>
                @if (canReply()) {
                  <rt-button
                    [disabled]="notesDeleteLoading.includes(note().id!)"
                    variant="plain"
                    (click)="showReply()"
                    class="reply"
                    size="small"
                  >
                    {{ notesTranslations.reply | translate }}
                  </rt-button>
                }
              </ng-template>
            </div>
          </div>
        }
      </div>
    }

    @if (editing()) {
      <div class="edit-form">
        <rt-note-form
          [active]="true"
          [note]="$any(note())"
          [noteToMention]="noteToMention()"
          [localStorageKeyBuilder]="localStorageKeyBuilder()"
          [attachmentsTemp]="attachmentsTemp ? attachmentsTemp[note().id!] : []"
          [noteSaveLoading]="noteSaveLoading.includes(note().id!)"
          [attachmentLoading]="
            !!(noteAttachmentLoading[note().id!] && noteAttachmentLoading[note().id!].length)
          "
          [canDeleteAttachments]="canDeleteAttachments"
          [visibilitySelectorComponent]="visibilitySelectorComponent()"
          [visibilityComponentInputs]="visibilityComponentInputs()"
          (cancelClick)="cancel()"
          (changeNoteVisibility)="changeNoteVisibilityClass($event)"
          (noteSaveClick)="noteSaveClick.emit($event)"
          (uploadAttachmentClick)="uploadAttachmentClick.emit($event)"
          (deleteAttachmentClick)="deleteAttachmentClick.emit($event)"
        />
      </div>
    }
  </div>

  @if (replying() && replyNote()) {
    <div class="reply-form note">
      <rt-note-form
        [active]="true"
        [noteToMention]="noteToMention()"
        [localStorageKeyBuilder]="localStorageKeyBuilder()"
        [replyToId]="$any(note().id)"
        [canDeleteAttachments]="canDeleteAttachments"
        [note]="$any(replyNote())"
        [allNotesFlat]="allNotesFlat"
        [autofocus]="true"
        [noteSaveLoading]="noteSaveLoading.includes(replyTempId())"
        [attachmentLoading]="
          !!(noteAttachmentLoading[replyTempId()] && noteAttachmentLoading[replyTempId()].length)
        "
        [attachmentsTemp]="attachmentsTemp ? attachmentsTemp[replyTempId()] : []"
        [visibilitySelectorComponent]="visibilitySelectorComponent()"
        [visibilityComponentInputs]="visibilityComponentInputs()"
        (changeNoteVisibility)="changeNoteVisibilityClass($event)"
        (typingStart)="replyTypingStart.emit({ replyToId: $any(replyTempId()) })"
        (typingFinish)="replyTypingFinish.emit()"
        (cancelClick)="cancel()"
        (noteSaveClick)="noteSaveClick.emit($event)"
        (uploadAttachmentClick)="uploadAttachmentClick.emit($event)"
        (replyChanges)="replyChanges.emit($event)"
        (deleteAttachmentClick)="deleteAttachmentClick.emit($event)"
      />
    </div>
  }

  @if (typingAdmins.length > 0) {
    <div class="typing-indicator">
      <rt-note-typing-indicator-message [typingAdmins]="typingAdmins" />
    </div>
  }

  @let noteValue = note();
  @if (noteValue.replies && noteValue.replies.length > 0) {
    <div class="replies">
      @for (reply of noteValue.replies; track reply.id!) {
        @if (allRepliesVisible || noteValue.replies.length < 6 || $first || $last) {
          <div class="reply">
            <rt-note-item
              [note]="reply"
              [noteIsReply]="true"
              [isOwnerFn]="isOwnerFn()"
              [getOwnerFn]="getOwnerFn()"
              [noteToMention]="noteToMention()"
              [localStorageKeyBuilder]="localStorageKeyBuilder()"
              [isAutomatedActionNoteFn]="isAutomatedActionNoteFn()"
              [editing]="notesBeingEdited.includes(reply.id!)"
              [replying]="notesBeingRepliedTo.includes(reply.id!)"
              [cachedReply]="cachedReply"
              [attachmentsTemp]="attachmentsTemp"
              [canDeleteAttachments]="canDeleteAttachments"
              [canHighLevelDataAccess]="canHighLevelDataAccess"
              [currentlyTypedNotes]="currentlyTypedNotes"
              [notesBeingRepliedTo]="notesBeingRepliedTo"
              [notesBeingEdited]="notesBeingEdited"
              [noteSaveLoading]="noteSaveLoading"
              [notesDeleteLoading]="notesDeleteLoading"
              [noteAttachmentLoading]="noteAttachmentLoading"
              [allNotesFlat]="allNotesFlat"
              [isReadOnly]="isReadOnly"
              [visibilitySelectorComponent]="visibilitySelectorComponent()"
              [visibilityComponentInputs]="visibilityComponentInputs()"
              [hasConnector]="!$last"
              (replyTypingStart)="replyTypingStart.emit($event)"
              (replyTypingFinish)="replyTypingFinish.emit()"
              (noteReplyClick)="noteReplyClick.emit($event)"
              (noteEditClick)="noteEditClick.emit($event)"
              (noteDiscardClick)="noteDiscardClick.emit($event)"
              (noteSaveClick)="noteSaveClick.emit($event)"
              (noteDeleteClick)="noteDeleteClick.emit($event)"
              (uploadAttachmentClick)="uploadAttachmentClick.emit($event)"
              (replyChanges)="replyChanges.emit($event)"
              (deleteAttachmentClick)="deleteAttachmentClick.emit($event)"
              (noteChecklistClick)="noteChecklistClick.emit($event)"
            />
          </div>
        }
        @if (!allRepliesVisible && note().replies.length > 5 && $index === 1) {
          <rt-thread-expander
            [text]="
              notesTranslations.showMoreReplies | translate: { count: note().replies.length - 2 }
            "
            [withDivider]="false"
            (expand)="showAllReplies()"
            class="thread-expander"
          />
        }
      }
    </div>
  }
</div>
<ng-template #actionButtons>
  @if (isEditable() && !note().pinnedAt) {
    <rt-icon-button
      [disabled]="notesDeleteLoading.includes(note().id!)"
      (click)="showEdit()"
      variant="plain"
      iconName="edit"
      shape="square"
      size="small"
    />
  }
  @if (moreButtonVisible) {
    <rt-icon-button
      #moreDropdownTrigger="rtOverlayOrigin"
      [rtTooltip]="notesTranslations.more | translate"
      [pressed]="moreDropdown.open"
      (click)="toggleDropdown(moreDropdown)"
      rt-overlay-origin
      variant="plain"
      iconName="more-vert"
      shape="square"
      size="small"
    />

    <ng-template
      #moreDropdown="rtConnectedOverlay"
      [origin]="moreDropdownTrigger"
      (backdropClick)="toggleDropdown(moreDropdown)"
      rt-connected-overlay
      hasBackdrop
    >
      <rt-dropdown [width]="DropdownWidth.SmallX">
        @if (!noteIsReply && !note().pinnedAt && canPin()) {
          <rt-dropdown-item (click)="pinNote(); toggleDropdown(moreDropdown)">
            <rt-icon prefix name="pin" />
            {{ notesTranslations.pin | translate }}
          </rt-dropdown-item>
        }
        @if (isEditable() && note().pinnedAt) {
          <rt-dropdown-item (click)="showEdit(); toggleDropdown(moreDropdown)">
            <rt-icon prefix name="edit" />
            {{ notesTranslations.edit | translate }}
          </rt-dropdown-item>
        }
        @if (isDeletable()) {
          <rt-dropdown-item (click)="deleteNote(); toggleDropdown(moreDropdown)">
            <rt-icon prefix name="delete" />
            {{ notesTranslations.delete | translate }}
          </rt-dropdown-item>
        }
      </rt-dropdown>
    </ng-template>
  }
</ng-template>
